export interface MetricData {
  name: string;
  value: number;
  timestamp: string;
  tags?: Record<string, string>;
}

export interface PerformanceMetric {
  page: string;
  loadTime: number;
  timestamp: string;
  userAgent: string;
  connectionType?: string;
}

export interface ErrorMetric {
  error: string;
  stack?: string;
  page: string;
  timestamp: string;
  userId?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface UserActionMetric {
  action: string;
  page: string;
  timestamp: string;
  userId?: string;
  metadata?: Record<string, any>;
}

class MonitoringService {
  private static instance: MonitoringService;
  private metrics: MetricData[] = [];
  private performanceMetrics: PerformanceMetric[] = [];
  private errorMetrics: ErrorMetric[] = [];
  private userActionMetrics: UserActionMetric[] = [];

  private constructor() {
    this.initializePerformanceObserver();
    this.initializeErrorTracking();
  }

  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  /**
   * Initialize Performance Observer for automatic performance tracking
   */
  private initializePerformanceObserver(): void {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            this.trackPageLoad(entry.name, navEntry.loadEventEnd - navEntry.loadEventStart);
          }
        }
      });

      observer.observe({ entryTypes: ['navigation'] });
    } catch (error) {
      console.warn('Performance Observer not supported:', error);
    }
  }

  /**
   * Initialize error tracking
   */
  private initializeErrorTracking(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Track unhandled errors
    window.addEventListener('error', (event) => {
      this.trackError({
        error: event.error?.message || event.message,
        stack: event.error?.stack,
        page: window.location.pathname,
        severity: 'high'
      });
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        error: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        page: window.location.pathname,
        severity: 'medium'
      });
    });
  }

  /**
   * Track custom metric
   */
  public trackMetric(name: string, value: number, tags?: Record<string, string>): void {
    const metric: MetricData = {
      name,
      value,
      timestamp: new Date().toISOString(),
      tags
    };

    this.metrics.push(metric);
    this.sendToMonitoringService(metric);
  }

  /**
   * Track page load performance
   */
  public trackPageLoad(page: string, loadTime: number): void {
    const metric: PerformanceMetric = {
      page,
      loadTime,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connectionType: (navigator as any).connection?.effectiveType
    };

    this.performanceMetrics.push(metric);
    this.sendToMonitoringService(metric);
  }

  /**
   * Track error
   */
  public trackError(errorData: Omit<ErrorMetric, 'timestamp'>): void {
    const metric: ErrorMetric = {
      ...errorData,
      timestamp: new Date().toISOString()
    };

    this.errorMetrics.push(metric);
    this.sendToMonitoringService(metric);

    // Send critical errors immediately
    if (metric.severity === 'critical') {
      this.sendAlert(metric);
    }
  }

  /**
   * Track user action
   */
  public trackUserAction(action: string, page: string, userId?: string, metadata?: Record<string, any>): void {
    const metric: UserActionMetric = {
      action,
      page,
      timestamp: new Date().toISOString(),
      userId,
      metadata
    };

    this.userActionMetrics.push(metric);
    this.sendToMonitoringService(metric);
  }

  /**
   * Track API call performance
   */
  public trackAPICall(endpoint: string, method: string, duration: number, status: number): void {
    this.trackMetric('api_call_duration', duration, {
      endpoint,
      method,
      status: status.toString()
    });

    if (status >= 400) {
      this.trackError({
        error: `API Error: ${method} ${endpoint} - ${status}`,
        page: window.location.pathname,
        severity: status >= 500 ? 'high' : 'medium'
      });
    }
  }

  /**
   * Track business metrics
   */
  public trackBusinessMetric(metric: string, value: number, userId?: string): void {
    this.trackMetric(metric, value, { userId: userId || 'anonymous' });
  }

  /**
   * Get performance summary
   */
  public getPerformanceSummary(): {
    averageLoadTime: number;
    totalPageLoads: number;
    slowestPages: PerformanceMetric[];
  } {
    const totalLoads = this.performanceMetrics.length;
    const averageLoadTime = totalLoads > 0 
      ? this.performanceMetrics.reduce((sum, metric) => sum + metric.loadTime, 0) / totalLoads
      : 0;

    const slowestPages = [...this.performanceMetrics]
      .sort((a, b) => b.loadTime - a.loadTime)
      .slice(0, 5);

    return {
      averageLoadTime,
      totalPageLoads: totalLoads,
      slowestPages
    };
  }

  /**
   * Get error summary
   */
  public getErrorSummary(): {
    totalErrors: number;
    criticalErrors: number;
    errorsBySeverity: Record<string, number>;
    recentErrors: ErrorMetric[];
  } {
    const totalErrors = this.errorMetrics.length;
    const criticalErrors = this.errorMetrics.filter(e => e.severity === 'critical').length;
    
    const errorsBySeverity = this.errorMetrics.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const recentErrors = [...this.errorMetrics]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);

    return {
      totalErrors,
      criticalErrors,
      errorsBySeverity,
      recentErrors
    };
  }

  /**
   * Send data to monitoring service
   */
  private async sendToMonitoringService(data: any): Promise<void> {
    try {
      // In production, send to actual monitoring service
      if (import.meta.env.NODE_ENV === 'production') {
        // Example: Send to Sentry, DataDog, or custom endpoint
        console.log('Sending to monitoring service:', data);
        
        // Uncomment when Sentry is properly configured
        // if (window.Sentry) {
        //   window.Sentry.captureMessage(JSON.stringify(data));
        // }
      }
    } catch (error) {
      console.warn('Failed to send metric to monitoring service:', error);
    }
  }

  /**
   * Send alert for critical issues
   */
  private async sendAlert(error: ErrorMetric): Promise<void> {
    try {
      // In production, send actual alerts
      if (import.meta.env.NODE_ENV === 'production') {
        console.error('CRITICAL ERROR ALERT:', error);
        
        // Example: Send to alerting service
        // await fetch('/api/alerts', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(error)
        // });
      }
    } catch (error) {
      console.warn('Failed to send alert:', error);
    }
  }

  /**
   * Get all metrics for debugging
   */
  public getAllMetrics(): {
    metrics: MetricData[];
    performance: PerformanceMetric[];
    errors: ErrorMetric[];
    userActions: UserActionMetric[];
  } {
    return {
      metrics: [...this.metrics],
      performance: [...this.performanceMetrics],
      errors: [...this.errorMetrics],
      userActions: [...this.userActionMetrics]
    };
  }

  /**
   * Clear old metrics to prevent memory leaks
   */
  public clearOldMetrics(maxAge: number = 24 * 60 * 60 * 1000): void {
    const cutoff = Date.now() - maxAge;
    
    this.metrics = this.metrics.filter(m => new Date(m.timestamp).getTime() > cutoff);
    this.performanceMetrics = this.performanceMetrics.filter(m => new Date(m.timestamp).getTime() > cutoff);
    this.errorMetrics = this.errorMetrics.filter(m => new Date(m.timestamp).getTime() > cutoff);
    this.userActionMetrics = this.userActionMetrics.filter(m => new Date(m.timestamp).getTime() > cutoff);
  }
}

export const monitoringService = MonitoringService.getInstance();
